import React, { useState, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Camera, RotateCcw, Check, X, Upload, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

interface IrisCameraCaptureProps {
  onCapture: (imageBlob: Blob, eyeSide: 'right' | 'left') => void;
  onUpload: (file: File, eyeSide: 'right' | 'left') => void;
  disabled?: boolean;
}

export const IrisCameraCapture: React.FC<IrisCameraCaptureProps> = ({
  onCapture,
  onUpload,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [capturedBlob, setCapturedBlob] = useState<Blob | null>(null);
  const [selectedEye, setSelectedEye] = useState<'right' | 'left'>('right');
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment');
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const startCamera = useCallback(async () => {
    try {
      const constraints = {
        video: {
          facingMode: facingMode,
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        setIsCameraActive(true);
      }
    } catch (error: any) {
      console.error("Erreur accès caméra:", error);
      toast.error("Impossible d'accéder à la caméra. Vérifiez les permissions.");
    }
  }, [facingMode]);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setIsCameraActive(false);
  }, []);

  const switchCamera = useCallback(() => {
    stopCamera();
    setFacingMode(prev => prev === 'user' ? 'environment' : 'user');
    setTimeout(() => startCamera(), 100);
  }, [stopCamera, startCamera]);

  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    if (!context) return;

    // Set canvas size to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw the current video frame
    context.drawImage(video, 0, 0);

    // Convert to blob
    canvas.toBlob((blob) => {
      if (blob) {
        setCapturedBlob(blob);
        setCapturedImage(URL.createObjectURL(blob));
        stopCamera();
      }
    }, 'image/jpeg', 0.95);
  }, [stopCamera]);

  const retakePhoto = useCallback(() => {
    setCapturedImage(null);
    setCapturedBlob(null);
    startCamera();
  }, [startCamera]);

  const confirmPhoto = useCallback(() => {
    if (capturedBlob) {
      onCapture(capturedBlob, selectedEye);
      setCapturedImage(null);
      setCapturedBlob(null);
      setIsOpen(false);
      toast.success(`Photo de l'${selectedEye === 'right' ? 'œil droit' : 'œil gauche'} enregistrée`);
    }
  }, [capturedBlob, selectedEye, onCapture]);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error("Veuillez sélectionner une image");
        return;
      }
      onUpload(file, selectedEye);
      toast.success(`Photo de l'${selectedEye === 'right' ? 'œil droit' : 'œil gauche'} importée`);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      stopCamera();
      setCapturedImage(null);
      setCapturedBlob(null);
    }
  };

  return (
    <div className="space-y-4">
      {/* Eye Selection */}
      <div className="flex gap-2 justify-center">
        <Button
          type="button"
          variant={selectedEye === 'right' ? 'default' : 'outline'}
          onClick={() => setSelectedEye('right')}
          className="gap-2"
        >
          <Eye className="w-4 h-4" />
          Œil droit
        </Button>
        <Button
          type="button"
          variant={selectedEye === 'left' ? 'default' : 'outline'}
          onClick={() => setSelectedEye('left')}
          className="gap-2"
        >
          <EyeOff className="w-4 h-4" />
          Œil gauche
        </Button>
      </div>

      {/* Action Buttons */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {/* Camera Capture Dialog */}
        <Dialog open={isOpen} onOpenChange={handleOpenChange}>
          <DialogTrigger asChild>
            <Button
              type="button"
              variant="outline"
              className="gap-2 h-auto py-4 flex-col"
              disabled={disabled}
              onClick={() => {
                setIsOpen(true);
                setTimeout(() => startCamera(), 100);
              }}
            >
              <Camera className="w-6 h-6" />
              <span>Prendre photo</span>
              <span className="text-xs text-muted-foreground">
                {selectedEye === 'right' ? 'Œil droit' : 'Œil gauche'}
              </span>
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Camera className="w-5 h-5" />
                Capture photo - {selectedEye === 'right' ? 'Œil droit' : 'Œil gauche'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Camera/Preview Area */}
              <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
                {capturedImage ? (
                  <img 
                    src={capturedImage} 
                    alt="Photo capturée" 
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <>
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      muted
                      className="w-full h-full object-contain"
                    />
                    {/* Circular guide overlay */}
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="w-48 h-48 sm:w-64 sm:h-64 rounded-full border-4 border-white/50 border-dashed">
                        <div className="absolute inset-0 flex items-center justify-center text-white/70 text-sm">
                          {!isCameraActive && "Chargement caméra..."}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Hidden canvas for capture */}
              <canvas ref={canvasRef} className="hidden" />

              {/* Controls */}
              <div className="flex justify-center gap-3">
                {capturedImage ? (
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={retakePhoto}
                      className="gap-2"
                    >
                      <RotateCcw className="w-4 h-4" />
                      Reprendre
                    </Button>
                    <Button
                      type="button"
                      onClick={confirmPhoto}
                      className="gap-2"
                    >
                      <Check className="w-4 h-4" />
                      Valider
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={switchCamera}
                      disabled={!isCameraActive}
                      className="gap-2"
                    >
                      <RotateCcw className="w-4 h-4" />
                      Changer caméra
                    </Button>
                    <Button
                      type="button"
                      onClick={capturePhoto}
                      disabled={!isCameraActive}
                      className="gap-2"
                      size="lg"
                    >
                      <Camera className="w-5 h-5" />
                      Capturer
                    </Button>
                  </>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Upload Button */}
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileUpload}
            className="hidden"
            id="iris-upload"
          />
          <Button
            type="button"
            variant="outline"
            className="gap-2 h-auto py-4 flex-col w-full"
            disabled={disabled}
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="w-6 h-6" />
            <span>Importer photo</span>
            <span className="text-xs text-muted-foreground">
              {selectedEye === 'right' ? 'Œil droit' : 'Œil gauche'}
            </span>
          </Button>
        </div>
      </div>
    </div>
  );
};
